'use client';

import { useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { RoomInfoPanel } from '@/components/public/room-info-panel';
import { ImageLightbox } from '@/components/public/image-lightbox';
import type { LightboxImage } from '@/components/public/image-lightbox';
import type { Room } from '@/types/database';

export default function Room39Content({ room }: { room: Room }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const t = useTranslations('roomContent.room-39');

  const GALLERY_IMAGES: LightboxImage[] = [
    { src: '/design-images/rooms/room-39/detail.jpg', alt: t('alt.detail'), width: 1920, height: 1372 },
    { src: '/design-images/uploads/1778231998838-02.jpg', alt: t('alt.post'), width: 1200, height: 800 },
    { src: '/design-images/rooms/room-39/gallery-2.jpg', alt: t('alt.gallery2'), width: 1920, height: 1279 },
    { src: '/design-images/rooms/room-39/gallery-4.jpg', alt: t('alt.gallery4'), width: 1920, height: 1282 },
    { src: '/design-images/uploads/1778232126612-39_ch_3.jpg', alt: t('alt.bathroom'), width: 1080, height: 1440 },
  ];

  return (
    <div id="room-content" className="w-full flex flex-col">
      {/* White spacer between hero and first section */}
      <div className="bg-white h-2 w-full" />

      {/* Section: Room Benefits */}
      <section className="bg-white w-full">
        <div className="max-w-[1500px] mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="w-full flex items-center py-6 px-6 md:px-8 lg:px-12">
              <div className="text-black w-full">
                <div className="tiptap-content text-base md:text-lg leading-relaxed">
                  <h1 className="font-heading font-bold mb-4 text-4xl md:text-5xl">
                    <strong>{t('benefits.titleLine1')}</strong>
                    <br />
                    <strong>{t('benefits.titleLine2')}</strong>
                  </h1>
                  <p className="mb-4">{t('benefits.p1')}</p>
                  <p className="mb-4">{t('benefits.p2')}</p>
                </div>
                <RoomInfoPanel room={room} />
              </div>
            </div>
            <div
              className="w-full h-full min-h-[400px] md:min-h-[500px] relative cursor-pointer"
              onClick={() => setLightboxIndex(0)}
            >
              <Image
                src="/design-images/rooms/room-39/detail.jpg"
                alt={t('alt.detail')}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
          </div>
        </div>
      </section>
      <div className="bg-white h-2 w-full" />

      {/* Section: Side-by-side */}
      <section className="bg-white w-full">

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div
              className="w-full h-full min-h-[400px] md:min-h-[500px] relative cursor-pointer"
              onClick={() => setLightboxIndex(1)}
            >
              <Image
                src="/design-images/uploads/1778231998838-02.jpg"
                alt={t('alt.post')}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
            <div className="w-full flex items-center py-8 md:py-12 px-6 md:px-8 lg:px-12">
              <div className="text-black w-full">
                <div className="tiptap-content text-base md:text-lg leading-relaxed">
                    <h1 className="font-heading font-bold mb-6 text-4xl md:text-5xl">{t('story.titleLine1')}<br /><strong>{t('story.titleLine2')}</strong></h1>
                    <p className="mb-4">{t('story.p1')}</p>
                    <p className="mb-4">{t('story.p2')}</p>
                    <p className="mb-4">{t('story.p3')}</p>
                    <p>{t('story.p4')}</p>
                </div>
              </div>
            </div>
        </div>

      </section>
      <div className="bg-white h-2 w-full" />

      {/* Paired images */}
      <div className="grid grid-cols-2 gap-0.5 mt-2">
        <div className="cursor-pointer" onClick={() => setLightboxIndex(2)}>
          <Image
            src="/design-images/rooms/room-39/gallery-2.jpg"
            alt={t('alt.gallery2')}
            width={1920}
            height={1279}
            className="w-full h-auto"
            sizes="50vw"
          />
        </div>
        <div className="cursor-pointer" onClick={() => setLightboxIndex(3)}>
          <Image
            src="/design-images/rooms/room-39/gallery-4.jpg"
            alt={t('alt.gallery4')}
            width={1920}
            height={1282}
            className="w-full h-auto"
            sizes="50vw"
          />
        </div>
      </div>
      <div className="bg-white h-2 w-full" />

      {/* Full-width bathroom image */}
      <div className="w-full flex justify-center bg-white px-4 py-6 md:py-8">
        <div className="cursor-pointer max-w-md w-full" onClick={() => setLightboxIndex(4)}>
          <Image
            src="/design-images/uploads/1778232126612-39_ch_3.jpg"
            alt={t('alt.bathroom')}
            width={1080}
            height={1440}
            className="w-full h-auto"
            sizes="(max-width: 768px) 90vw, 400px"
          />
        </div>
      </div>

      {/* Fullscreen gallery lightbox */}
      {lightboxIndex !== null && (
        <ImageLightbox
          images={GALLERY_IMAGES}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
